import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  CaretLeft, Target, Barbell, Trophy, Heart, Note, FloppyDisk, PencilSimple,
  ArrowUpRight, User, Ruler, Bandaids, Books, VideoCamera, FileText,
  ChartLineUp, Bed, Waveform, Warning, CalendarBlank, ClockCounterClockwise, Plus,
  UploadSimple, Trash, ChatCircleDots, Users, Heartbeat, Pulse, Lightning, Medal,
  DownloadSimple, X, Fire,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatPace, parseClock, fmtClock } from "@/lib/pace";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import MessagesThread from "@/components/MessagesThread";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveMedia = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};

const SECTIONS = [
  { id: "overview",     label: "Overview",     icon: User },
  { id: "readiness",    label: "Readiness",    icon: Heartbeat },
  { id: "performance",  label: "Performance",  icon: ChartLineUp },
  { id: "programs",     label: "Programs",     icon: Barbell },
  { id: "recovery",     label: "Recovery",     icon: Heart },
  { id: "checkins",     label: "Check-ins",    icon: Fire },
  { id: "medical",      label: "Medical",      icon: Bandaids },
  { id: "competitions", label: "Competitions", icon: Trophy },
  { id: "documents",    label: "Documents",    icon: FileText },
  { id: "media",        label: "Media",        icon: VideoCamera },
  { id: "notes",        label: "Coach Notes",  icon: Note },
  { id: "guardians",    label: "Guardians",    icon: Users },
  { id: "timeline",     label: "Timeline",     icon: ClockCounterClockwise },
];

const fmtDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return String(v); }
};

const daysUntil = (v) => {
  if (!v) return null;
  try {
    const t = new Date(v).getTime();
    const d = new Date().getTime();
    return Math.ceil((t - d) / (1000 * 60 * 60 * 24));
  } catch { return null; }
};

function GuardianInvite({ clientId, clientName }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [relationship, setRelationship] = useState("parent");
  const [sending, setSending] = useState(false);
  const [invites, setInvites] = useState([]);

  const REL_LABEL = { parent: "Parent", guardian: "Guardian", coach_contact: "Coach's contact" };
  const STATUS_CLS = {
    pending: "text-amber-400",
    active: "text-emerald-400",
    declined: "text-red-400",
    revoked: "text-text-secondary",
  };

  const loadInvites = () => {
    api.get(`/coach/clients/${clientId}/guardian-invites`)
      .then(({ data }) => setInvites(data.items || []))
      .catch(() => setInvites([]));
  };
  useEffect(() => { if (open) loadInvites(); }, [open]);

  const submit = async () => {
    if (!email.trim()) return;
    setSending(true);
    try {
      await api.post(`/coach/clients/${clientId}/guardian-invite`, { email: email.trim(), relationship });
      toast.success(`Guardian request sent — ${clientName || "the athlete"} must confirm before access is granted`);
      setEmail("");
      loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite");
    } finally {
      setSending(false);
    }
  };

  const cancel = async (linkId) => {
    try {
      await api.delete(`/coach/clients/${clientId}/guardian-invites/${linkId}`);
      toast.success("Invite cancelled");
      loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="coach-invite-guardian-open"
        className="inline-flex items-center gap-1.5 border border-white/20 text-white px-4 py-2 text-xs label-tiny tracking-wider hover:border-brand-accent transition"
      >
        <Users size={13} weight="duotone" /> INVITE GUARDIAN
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="coach-invite-guardian-modal">
          <div className="tactical-card p-6 max-w-md w-full space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="label-tiny text-brand-accent mb-1">// INVITE A PARENT / GUARDIAN</div>
                <h3 className="font-heading text-lg leading-tight">FOR {(clientName || "THIS ATHLETE").toUpperCase()}</h3>
              </div>
              <button onClick={() => setOpen(false)} data-testid="coach-invite-guardian-close" className="text-text-secondary hover:text-white"><X size={18} /></button>
            </div>
            <p className="text-xs text-text-secondary">
              Enter the guardian&apos;s Edge Training account email. This sends a request that
              <span className="text-white"> {clientName || "the athlete"} must confirm</span> — no data
              is shared until they accept.
            </p>
            <div>
              <label className="label-tiny block mb-1.5">GUARDIAN EMAIL</label>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="parent@example.com" data-testid="coach-invite-guardian-email"
                onKeyDown={(e) => e.key === "Enter" && submit()}
              />
            </div>
            <div>
              <label className="label-tiny block mb-1.5">RELATIONSHIP</label>
              <select
                value={relationship} onChange={(e) => setRelationship(e.target.value)}
                data-testid="coach-invite-guardian-relationship"
                className="w-full bg-surface-elevated border border-white/15 px-3 py-2.5 text-sm text-white focus:border-brand-accent outline-none"
              >
                <option value="parent" className="bg-surface-DEFAULT">Parent</option>
                <option value="guardian" className="bg-surface-DEFAULT">Guardian</option>
                <option value="coach_contact" className="bg-surface-DEFAULT">Coach&apos;s contact</option>
              </select>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={() => setOpen(false)} data-testid="coach-invite-guardian-cancel"
                className="flex-1 py-2.5 text-xs label-tiny tracking-widest border border-white/20 text-text-secondary hover:border-white/40 transition-colors">CANCEL</button>
              <button onClick={submit} disabled={sending || !email.trim()} data-testid="coach-invite-guardian-submit"
                className="flex-1 py-2.5 text-xs label-tiny tracking-widest btn-kinetic disabled:opacity-50">
                {sending ? "SENDING…" : "SEND REQUEST"}
              </button>
            </div>

            {invites.length > 0 && (
              <div className="border-t border-white/10 pt-4 space-y-2" data-testid="coach-guardian-invites-list">
                <div className="label-tiny text-text-secondary">// INVITES SENT</div>
                {invites.map((iv) => (
                  <div key={iv.link_id} className="flex items-center gap-3 border border-white/10 bg-surface-elevated/40 px-3 py-2" data-testid={`coach-guardian-invite-${iv.link_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{iv.parent_name || iv.parent_email}</div>
                      <div className="text-[10px] tracking-widest text-text-secondary">{REL_LABEL[iv.relationship] || iv.relationship}</div>
                    </div>
                    <span className={`label-tiny text-[10px] ${STATUS_CLS[iv.status] || "text-text-secondary"}`} data-testid={`coach-guardian-invite-status-${iv.link_id}`}>
                      {iv.status === "active" ? "CONFIRMED" : iv.status.toUpperCase()}
                    </span>
                    {iv.status === "pending" && (
                      <button onClick={() => cancel(iv.link_id)} className="text-text-secondary hover:text-red-400" data-testid={`coach-guardian-invite-cancel-${iv.link_id}`} title="Cancel invite">
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}


export default function ClientDetail() {
  const { client_id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [assessment, setAssessment] = useState(null);
  const [history, setHistory] = useState([]);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [active, setActive] = useState("overview");
  const [medical, setMedical] = useState({ restrictions: [], pain_log: [], documents: [] });
  const [competitions, setCompetitions] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [media, setMedia] = useState([]);
  const [journal, setJournal] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [timeline, setTimeline] = useState({ events: [], counts: {}, total: 0 });
  const [tests, setTests] = useState([]);
  const [guardians, setGuardians] = useState({ active: [], pending: [] });
  const [cardioTrends, setCardioTrends] = useState([]);
  const [checkins, setCheckins] = useState(null);

  const load = () => {
    api.get(`/coach/clients/${client_id}`).then(({ data }) => { setData(data); setNotes(data.link?.notes || ""); });
    api.get(`/coach/clients/${client_id}/cardio-trends`).then(({ data }) => setCardioTrends(data.movements || [])).catch(() => {});
    api.get(`/coach/clients/${client_id}/checkins`).then(({ data }) => setCheckins(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/assessment`).then(({ data }) => setAssessment(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/assessment/history`).then(({ data }) => setHistory(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/medical`).then(({ data }) => setMedical(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/competitions`).then(({ data }) => setCompetitions(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/documents`).then(({ data }) => setDocuments(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/media`).then(({ data }) => setMedia(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/notes`).then(({ data }) => setJournal(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/readiness`).then(({ data }) => setReadiness(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/timeline?limit=200`).then(({ data }) => setTimeline(data)).catch(() => {});
    api.get(`/coach/clients/${client_id}/tests?limit=500`).then(({ data }) => setTests(data || [])).catch(() => {});
    api.get(`/coach/clients/${client_id}/guardians`).then(({ data }) => setGuardians(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [client_id]);

  // Scroll spy on section anchors
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-25% 0px -60% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [data]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try { await api.patch(`/coach/clients/${client_id}/notes`, { notes }); toast.success("Notes saved"); }
    catch { toast.error("Failed"); }
    finally { setSavingNotes(false); }
  };

  if (!data) return <div className="p-10 label-tiny text-text-secondary">Loading...</div>;

  const { client, workouts = [], prs = [], goals = [], recovery = [], sleep_logs = [], form_reviews = [], assignments = [], total_workouts = 0 } = data;
  const latestRecovery = recovery[0];
  const currentAssignments = assignments.filter((a) => a.status === "active");
  const previousAssignments = assignments.filter((a) => a.status !== "active");

  return (
    <div className="max-w-[1400px] mx-auto p-6 lg:p-10 overflow-x-clip" data-testid="client-detail-page">
      <Link to="/coach/clients" className="text-xs label-tiny text-text-secondary hover:text-white inline-flex items-center gap-1 mb-4" data-testid="back-link">
        <CaretLeft size={12} /> BACK TO ROSTER
      </Link>

      {/* Athlete header */}
      <div className="flex items-start gap-5 mb-6 flex-wrap">
        {client.picture ? (
          <img src={client.picture} alt="" className="w-24 h-24 rounded-full object-cover" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-brand-primary flex items-center justify-center font-heading text-4xl">
            {client.name?.[0]?.toUpperCase()}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="label-tiny text-brand-accent mb-1">// ATHLETE FILE</div>
          <h1 className="font-heading text-4xl sm:text-5xl">{client.name?.toUpperCase()}</h1>
          <p className="text-text-secondary text-sm mt-1">{client.email}{assessment?.general?.sport ? <span className="ml-3">· {assessment.general.sport}</span> : null}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ReportButton clientId={client_id} clientName={client.name} />
          <GuardianInvite clientId={client_id} clientName={client.name} />
          <Link
            to={`/coach/clients/${client_id}/assessment`}
            className="inline-flex items-center gap-1.5 bg-brand-accent text-surface-base px-4 py-2 text-xs label-tiny tracking-wider hover:opacity-90 transition"
            data-testid="open-advanced-assessment"
          >
            FULL ASSESSMENT <ArrowUpRight size={12} weight="bold" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-8">
        {/* Anchor nav */}
        <nav className="lg:sticky lg:top-6 lg:self-start" data-testid="section-nav">
          <div className="label-tiny text-text-secondary mb-3 hidden lg:block">// FILE INDEX</div>
          <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                data-testid={`nav-${s.id}`}
                className={`inline-flex items-center gap-2 px-3 py-2 text-xs label-tiny tracking-wider text-left whitespace-nowrap border-l-2 transition ${
                  active === s.id
                    ? "border-brand-accent bg-brand-accent/[0.08] text-brand-accent"
                    : "border-transparent text-text-secondary hover:text-white hover:bg-white/[0.03]"
                }`}
              >
                <s.icon size={12} weight="duotone" /> {s.label.toUpperCase()}
              </button>
            ))}
          </div>
        </nav>

        {/* Sections */}
        <div className="space-y-10 min-w-0">
          <OverviewSection id="overview" client={client} assessment={assessment} totalWorkouts={total_workouts} coach={user} link={data.link} goals={goals} />
          <ReadinessSection id="readiness" readiness={readiness} />
          <PerformanceSection id="performance" prs={prs} history={history} assessment={assessment} tests={tests} cardioTrends={cardioTrends} />
          <ProgramsSection id="programs" current={currentAssignments} previous={previousAssignments} completions={data.completions || []} clientId={client_id} reload={load} />
          <RecoverySection id="recovery" recovery={recovery} sleep={sleep_logs} assessment={assessment} latest={latestRecovery} />
          <CheckinsSection id="checkins" checkins={checkins} clientName={client?.name} />
          <MedicalSection id="medical" clientId={client_id} assessment={assessment} medical={medical} reload={load} />
          <CompetitionsSection id="competitions" clientId={client_id} assessment={assessment} competitions={competitions} reload={load} />
          <DocumentsSection id="documents" clientId={client_id} documents={documents} reload={load} />
          <MediaSection id="media" clientId={client_id} formReviews={form_reviews} media={media} reload={load} />
          <CoachNotesSection id="notes" clientId={client_id} notes={notes} setNotes={setNotes} save={saveNotes} saving={savingNotes} linkId={data.link?.link_id} currentUserId={user?.user_id} journal={journal} reload={load} />
          <GuardiansSection id="guardians" guardians={guardians} />
          <TimelineSection id="timeline" timeline={timeline} />
        </div>
      </div>
    </div>
  );
}

/* ----------------------- REPORT BUTTON (Module 8) ----------------------- */
const REPORT_WINDOWS = [
  { value: "30d", label: "Last 30 Days" },
  { value: "90d", label: "Last 90 Days" },
  { value: "1y",  label: "Last Year" },
  { value: "all", label: "All Time" },
];

function ReportButton({ clientId, clientName }) {
  const [open, setOpen] = useState(false);
  const [window, setWindow] = useState("90d");
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/coach/clients/${clientId}/report/pdf?window=${window}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `EdgeTraining_Report_${(clientName || "athlete").replace(/\s+/g, "_")}_${window.toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Report ready");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Report failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 border border-brand-heat text-brand-heat px-4 py-2 text-xs label-tiny tracking-wider hover:bg-brand-heat hover:text-white transition"
        data-testid="open-report-dialog"
      >
        <DownloadSimple size={12} weight="bold" /> GENERATE REPORT
      </button>
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !busy && setOpen(false)}
          data-testid="report-dialog"
        >
          <div className="bg-white border border-slate-200 shadow-xl max-w-md w-full p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="label-tiny text-brand-heat text-[10px]">// BRANDED ATHLETE REPORT</div>
              <h3 className="font-heading text-2xl mt-1">{clientName || "Athlete"}</h3>
              <p className="text-[11px] text-slate-600 mt-1">
                PDF covers workouts, PBs, attendance &amp; adherence, test progression graphs, upcoming meets, and coach comments in the chosen window.
              </p>
            </div>
            <div className="space-y-1.5">
              {REPORT_WINDOWS.map((w) => (
                <label
                  key={w.value}
                  className={`flex items-center gap-3 border p-3 cursor-pointer transition ${
                    window === w.value ? "border-brand-heat bg-orange-50" : "border-slate-200 hover:border-slate-400"
                  }`}
                  data-testid={`report-window-${w.value}`}
                >
                  <input
                    type="radio"
                    name="report_window"
                    value={w.value}
                    checked={window === w.value}
                    onChange={() => setWindow(w.value)}
                    className="accent-brand-heat"
                  />
                  <span className="text-sm flex-1 text-slate-800">{w.label}</span>
                  <span className="label-tiny text-[9px] text-slate-500">{w.value.toUpperCase()}</span>
                </label>
              ))}
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => setOpen(false)}
                disabled={busy}
                className="text-xs label-tiny tracking-wider px-4 py-2 border border-slate-300 text-slate-700 hover:border-slate-500 disabled:opacity-40"
                data-testid="report-cancel"
              >CANCEL</button>
              <button
                onClick={download}
                disabled={busy}
                className="text-xs label-tiny tracking-wider px-4 py-2 bg-brand-heat text-white hover:bg-brand-heatHover disabled:opacity-50 inline-flex items-center gap-1.5"
                data-testid="report-download"
              >
                {busy ? "BUILDING..." : <><DownloadSimple size={12} weight="bold" /> DOWNLOAD PDF</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ----------------------- Section shell ----------------------- */
function Section({ id, title, icon: Icon, action, children }) {
  return (
    <section id={id} data-testid={`section-${id}`} className="scroll-mt-24">
      <div className="flex items-center justify-between gap-3 mb-4 pb-3 border-b border-white/10">
        <div className="flex items-center gap-2">
          <Icon size={16} weight="duotone" className="text-brand-accent" />
          <h2 className="font-heading text-2xl">{title.toUpperCase()}</h2>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function EmptyRow({ text }) {
  return <div className="text-sm text-text-secondary py-6 text-center border border-dashed border-white/10">{text}</div>;
}

/* ----------------------- GUARDIANS ----------------------- */
function GuardiansSection({ id, guardians }) {
  const REL = { parent: "Parent", guardian: "Guardian", coach_contact: "Coach's contact" };
  const active = guardians?.active || [];
  const pending = guardians?.pending || [];
  return (
    <Section id={id} title="Guardians" icon={Users}>
      <p className="text-xs text-text-secondary mb-4">
        Parents / guardians connected to this athlete. Access is always confirmed by the athlete — read-only.
      </p>

      <div className="label-tiny text-text-secondary mb-2">// LINKED ({active.length})</div>
      {active.length === 0 ? (
        <EmptyRow text="No confirmed guardians yet." />
      ) : (
        <div className="space-y-2 mb-6" data-testid="guardians-active">
          {active.map((g) => (
            <div key={g.parent_id} className="flex items-center gap-3 border border-white/10 bg-surface-elevated/40 px-4 py-3" data-testid={`guardian-active-${g.parent_id}`}>
              <div className="w-9 h-9 bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-sm font-heading text-amber-300 shrink-0">
                {(g.name || "G").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{g.name || g.email}</div>
                <div className="text-[10px] tracking-widest text-text-secondary truncate">{g.email}</div>
              </div>
              <span className="label-tiny text-[10px] text-brand-accent">{REL[g.relationship] || g.relationship}</span>
              {g.initiated_by === "coach" && <span className="label-tiny text-[9px] text-text-secondary">VIA COACH</span>}
            </div>
          ))}
        </div>
      )}

      <div className="label-tiny text-text-secondary mb-2">// PENDING ({pending.length})</div>
      {pending.length === 0 ? (
        <EmptyRow text="No pending guardian requests." />
      ) : (
        <div className="space-y-2" data-testid="guardians-pending">
          {pending.map((p) => (
            <div key={p.link_id} className="flex items-center gap-3 border border-amber-400/40 bg-amber-500/10 px-4 py-3" data-testid={`guardian-pending-${p.link_id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{p.parent_name || p.parent_email}</div>
                <div className="text-[10px] tracking-widest text-text-secondary truncate">
                  {REL[p.relationship] || p.relationship}
                  {p.initiated_by === "coach" ? ` · invited by ${p.coach_name || "coach"}` : " · requested by guardian"}
                </div>
              </div>
              <span className="label-tiny text-[10px] text-amber-400">AWAITING ATHLETE</span>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}


/* ----------------------- OVERVIEW ----------------------- */
function OverviewSection({ id, client, assessment, totalWorkouts, coach, link, goals }) {
  const g = assessment?.general || {};
  const compDate = assessment?.competition_date;
  const compDays = daysUntil(compDate);
  const status = assessment?.status || (assessment?.movement_screen && Object.values(assessment.movement_screen).some((m) => m?.pain) ? "flagged" : "cleared");
  return (
    <Section id={id} title="Overview" icon={User}>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        <Card label="AGE" value={g.age || "—"} data-testid="ov-age" />
        <Card label="SPORT" value={g.sport || "—"} data-testid="ov-sport" />
        <Card label="EVENT" value={g.event || "—"} data-testid="ov-event" />
        <Card label="POSITION" value={g.playing_position || "—"} data-testid="ov-position" />
        <Card label="TRAINING AGE" value={g.training_age ? `${g.training_age} yr` : "—"} data-testid="ov-training-age" />
        <Card label="COACH" value={coach?.name || coach?.email || "—"} data-testid="ov-coach" />
        <Card
          label="NEXT COMPETITION"
          value={compDays != null ? `${compDays >= 0 ? compDays : Math.abs(compDays)} d ${compDays >= 0 ? "to go" : "ago"}` : "—"}
          hint={fmtDate(compDate)}
          accent={compDays != null && compDays <= 30 && compDays >= 0}
          data-testid="ov-competition"
        />
        <Card
          label="STATUS"
          value={status === "cleared" ? "CLEARED" : status.toUpperCase()}
          color={status === "cleared" ? "green" : status === "flagged" ? "red" : "orange"}
          data-testid="ov-status"
        />
      </div>

      {(g.goals || goals.length > 0) && (
        <div className="mt-6">
          <div className="label-tiny mb-2">// GOALS</div>
          {g.goals ? (
            <div className="border border-white/10 p-4 text-sm whitespace-pre-wrap" data-testid="ov-goals-text">{g.goals}</div>
          ) : null}
          {goals.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2" data-testid="ov-active-goals">
              {goals.map((goal) => (
                <div key={goal.goal_id} className="border border-white/5 p-3">
                  <div className="text-sm font-heading">{goal.title}</div>
                  <div className="text-xs text-text-secondary mt-1 tabular-nums">{goal.current_value} / {goal.target_value} {goal.unit}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-6 text-xs text-text-secondary tabular-nums">
        <span className="mr-4"><span className="text-white">{totalWorkouts}</span> lifetime workouts</span>
        <span>Client since <span className="text-white">{fmtDate(link?.created_at)}</span></span>
      </div>
    </Section>
  );
}

function Card({ label, value, hint, accent, color = "white", "data-testid": tid }) {
  const colors = { white: "text-white", green: "text-green-500", orange: "text-brand-accent", red: "text-red-500" };
  return (
    <div className={`border p-4 ${accent ? "border-brand-accent bg-brand-accent/[0.05]" : "border-white/10"}`} data-testid={tid}>
      <div className="label-tiny text-[9px] text-text-secondary">{label}</div>
      <div className={`font-heading text-2xl mt-1 ${colors[color]}`}>{value}</div>
      {hint && <div className="text-[10px] text-text-secondary mt-1">{hint}</div>}
    </div>
  );
}

/* ----------------------- PERFORMANCE ----------------------- */
function PerformanceSection({ id, prs, history, assessment, tests, cardioTrends }) {
  const [view, setView] = useState("overview"); // overview | graphs
  const testResults = useMemo(() => {
    const rows = [];
    const pushDict = (cat, dict) => {
      if (!dict || typeof dict !== "object") return;
      Object.entries(dict).forEach(([k, v]) => {
        if (v && typeof v === "object" && !Array.isArray(v)) {
          const best = v.best_attempt ?? v.est_1rm ?? v.value ?? v.result;
          rows.push({ category: cat, name: k, value: best != null ? String(best) : "—", detail: v.protocol || "" });
        } else if (v != null && v !== "") {
          rows.push({ category: cat, name: k, value: String(v), detail: "" });
        }
      });
    };
    pushDict("Strength", assessment?.strength);
    pushDict("Power",    assessment?.power);
    pushDict("Speed",    assessment?.speed);
    pushDict("Agility",  assessment?.agility);
    pushDict("Conditioning", assessment?.conditioning);
    pushDict("Sport-Specific", assessment?.sport_specific);
    return rows;
  }, [assessment]);

  return (
    <Section id={id} title="Performance" icon={ChartLineUp}
      action={
        <div className="flex items-center gap-1" data-testid="perf-view-tabs">
          <button
            onClick={() => setView("overview")}
            data-testid="perf-view-overview"
            className={`px-3 py-1 text-[10px] label-tiny tracking-wider border ${view === "overview" ? "bg-brand-accent text-surface-base border-brand-accent" : "border-white/15 text-text-secondary hover:border-white/40"}`}
          >OVERVIEW</button>
          <button
            onClick={() => setView("graphs")}
            data-testid="perf-view-graphs"
            className={`px-3 py-1 text-[10px] label-tiny tracking-wider border ${view === "graphs" ? "bg-brand-accent text-surface-base border-brand-accent" : "border-white/15 text-text-secondary hover:border-white/40"}`}
          >GRAPHS</button>
          <Link to={`assessment`} className="ml-2 text-xs label-tiny text-brand-accent hover:underline" data-testid="perf-open-full">FULL ↗</Link>
        </div>
      }
    >
      {view === "overview" && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div>
              <div className="label-tiny mb-2">// PERSONAL RECORDS ({prs.length})</div>
              {prs.length === 0 ? <EmptyRow text="No PRs yet." /> : (
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1" data-testid="perf-pr-list">
                  {prs.map((pr) => (
                    <div key={pr.pr_id} className="border border-white/5 p-3">
                      <div className="text-xs text-text-secondary">{pr.exercise}</div>
                      <div className="font-heading text-lg text-brand-accent tabular-nums">{pr.value}<span className="text-xs text-text-secondary ml-1">{pr.unit}</span></div>
                      <div className="text-[10px] text-text-secondary mt-1">{fmtDate(pr.date)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="label-tiny mb-2">// TEST RESULTS ({testResults.length})</div>
              {testResults.length === 0 ? <EmptyRow text="Save assessment tests to see them here." /> : (
                <div className="border border-white/10 divide-y divide-white/5 max-h-64 overflow-y-auto" data-testid="perf-test-results">
                  {testResults.map((t, i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2">
                      <div>
                        <div className="text-[10px] label-tiny text-text-secondary">{t.category.toUpperCase()}</div>
                        <div className="text-sm">{t.name}</div>
                      </div>
                      <div className="font-heading text-lg text-brand-accent tabular-nums">{t.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4">
            <div className="label-tiny mb-2">// PROGRESSION SNAPSHOTS ({history.length})</div>
            {history.length === 0
              ? <EmptyRow text="No assessment history yet. Save the first Full Assessment to build the graph." />
              : <MiniTrend history={history} />}
          </div>
        </>
      )}

      {view === "graphs" && <PerformanceGraphsTab prs={prs} history={history} tests={tests || []} cardioTrends={cardioTrends || []} />}
    </Section>
  );
}

/* ---------------- Consolidated performance graphs ---------------- */
function PerformanceGraphsTab({ prs, history, tests, cardioTrends = [] }) {
  const [range, setRange] = useState("all"); // 30d | 90d | 1y | all
  const cutoffMs = useMemo(() => {
    if (range === "all") return 0;
    const days = { "30d": 30, "90d": 90, "1y": 365 }[range] || 0;
    return Date.now() - days * 86400000;
  }, [range]);
  const inRange = (dateStr) => {
    if (!cutoffMs) return true;
    if (!dateStr) return false;
    return new Date(dateStr).getTime() >= cutoffMs;
  };

  // Group structured tests by name (only tests with numeric best_score)
  const testGroups = useMemo(() => {
    const g = {};
    (tests || []).forEach((t) => {
      const v = Number(t.best_score);
      if (!Number.isFinite(v)) return;
      if (!inRange(t.test_date)) return;
      const key = t.test_name || "Test";
      if (!g[key]) g[key] = { name: key, unit: t.unit, higher: (t.higher_is_better !== false), rows: [] };
      g[key].rows.push({ date: t.test_date, value: v, pb: !!t.is_personal_best });
    });
    Object.values(g).forEach((grp) => grp.rows.sort((a, b) => (a.date || "").localeCompare(b.date || "")));
    return Object.values(g).filter((g0) => g0.rows.length > 0);
  }, [tests, cutoffMs]);

  // Group PRs by exercise (numeric only)
  const prGroups = useMemo(() => {
    const g = {};
    (prs || []).forEach((p) => {
      const v = Number(p.value);
      if (!Number.isFinite(v)) return;
      if (!inRange(p.date)) return;
      const key = p.exercise || "Lift";
      if (!g[key]) g[key] = { name: key, unit: p.unit || "", rows: [] };
      g[key].rows.push({ date: p.date, value: v });
    });
    Object.values(g).forEach((grp) => grp.rows.sort((a, b) => (a.date || "").localeCompare(b.date || "")));
    return Object.values(g).filter((g0) => g0.rows.length >= 1);
  }, [prs, cutoffMs]);

  // Snapshot series
  const snapshotHistory = useMemo(() => (history || []).filter((h) => inRange(h.date || h.created_at)), [history, cutoffMs]);

  // Cardio pace per movement (pace = s/100m; lower is better). Filter by range.
  const cardioGroups = useMemo(() => {
    return (cardioTrends || [])
      .map((m) => ({
        name: m.name,
        unit: m.unit || "s/100m",
        rows: (m.rows || []).filter((r) => inRange(r.date)).map((r) => ({ date: r.date, value: r.value })),
      }))
      .filter((m) => m.rows.length >= 1);
  }, [cardioTrends, cutoffMs]);

  return (
    <div data-testid="perf-graphs-tab">
      <div className="flex items-center gap-1 mb-4" data-testid="perf-range-tabs">
        {[["30d", "30D"], ["90d", "90D"], ["1y", "1Y"], ["all", "ALL"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setRange(k)}
            data-testid={`perf-range-${k}`}
            className={`px-3 py-1 text-[10px] label-tiny tracking-wider border ${range === k ? "bg-brand-accent text-surface-base border-brand-accent" : "border-white/15 text-text-secondary hover:border-white/40"}`}
          >{label}</button>
        ))}
      </div>

      {/* Structured tests */}
      <div className="mb-6">
        <div className="label-tiny mb-2">// TEST PROGRESSION ({testGroups.length})</div>
        {testGroups.length === 0 ? (
          <EmptyRow text="No structured test logs in this range. Log tests from the Full Assessment." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {testGroups.map((g) => (
              <LineChartCard key={g.name} title={g.name} unit={g.unit} rows={g.rows} higher={g.higher} testId={`test-graph-${g.name.replace(/\s+/g, "-")}`} />
            ))}
          </div>
        )}
      </div>

      {/* PRs */}
      <div className="mb-6">
        <div className="label-tiny mb-2">// PR PROGRESSION ({prGroups.length})</div>
        {prGroups.length === 0 ? (
          <EmptyRow text="No PRs in this range." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {prGroups.map((g) => (
              <LineChartCard key={g.name} title={g.name} unit={g.unit} rows={g.rows} higher testId={`pr-graph-${g.name.replace(/\s+/g, "-")}`} />
            ))}
          </div>
        )}
      </div>

      {/* Cardio pace — lower is faster */}
      <div className="mb-6">
        <div className="label-tiny mb-2">// CARDIO PACE · LOWER IS FASTER ({cardioGroups.length})</div>
        {cardioGroups.length === 0 ? (
          <EmptyRow text="No cardio/carry sessions logged in this range." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {cardioGroups.map((g) => (
              <LineChartCard key={g.name} title={g.name} unit={g.unit} rows={g.rows} higher={false} testId={`cardio-graph-${g.name.replace(/\s+/g, "-")}`} />
            ))}
          </div>
        )}
      </div>

      {/* Snapshot mini-charts */}
      <div>
        <div className="label-tiny mb-2">// ASSESSMENT SNAPSHOTS ({snapshotHistory.length})</div>
        {snapshotHistory.length === 0 ? <EmptyRow text="No snapshots in this range." /> : <MiniTrend history={snapshotHistory} />}
      </div>
    </div>
  );
}

function LineChartCard({ title, unit, rows, higher, testId }) {
  if (!rows || rows.length === 0) return null;
  const vals = rows.map((r) => r.value);
  const max = Math.max(...vals);
  const min = Math.min(...vals);
  const span = Math.max(max - min, 0.01);
  const w = 260, h = 70, pad = 4;
  const stepX = rows.length > 1 ? (w - pad * 2) / (rows.length - 1) : 0;
  const y = (v) => h - pad - ((v - min) / span) * (h - pad * 2);
  const points = rows.map((r, i) => `${pad + i * stepX},${y(r.value)}`).join(" ");
  const last = rows[rows.length - 1].value;
  const first = rows[0].value;
  const delta = last - first;
  const deltaGood = higher ? delta >= 0 : delta <= 0;

  return (
    <div className="border border-white/10 p-3" data-testid={testId}>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-sm font-heading truncate">{title}</div>
        <div className="text-[10px] text-text-secondary tabular-nums">{rows.length} pt{rows.length === 1 ? "" : "s"}</div>
      </div>
      <div className="flex items-baseline justify-between mb-2">
        <div className="font-heading text-2xl text-brand-accent tabular-nums">{last}<span className="text-xs text-text-secondary ml-1">{unit}</span></div>
        {rows.length > 1 && (
          <div className={`text-[10px] label-tiny tabular-nums ${deltaGood ? "text-green-500" : "text-red-500"}`}>
            {delta >= 0 ? "+" : ""}{delta.toFixed(2)} {unit}
          </div>
        )}
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-16" preserveAspectRatio="none">
        <polyline points={points} fill="none" stroke="currentColor" strokeWidth="1.5" className="text-brand-accent" />
        {rows.map((r, i) => (
          <circle key={i} cx={pad + i * stepX} cy={y(r.value)} r={r.pb ? 3 : 2} className={r.pb ? "fill-yellow-400" : "fill-brand-accent"} />
        ))}
      </svg>
      <div className="flex justify-between text-[9px] text-text-secondary mt-1 tabular-nums">
        <span>{fmtDate(rows[0].date)}</span>
        <span>{fmtDate(rows[rows.length - 1].date)}</span>
      </div>
    </div>
  );
}

function MiniTrend({ history }) {
  const series = [
    ["performance_score", "PERF SCORE"],
    ["power.cmj_cm", "CMJ (cm)"],
    ["speed.s10m", "10m SPRINT (s)"],
    ["recovery_metrics.hrv_ms", "HRV (ms)"],
  ];
  const pick = (o, p) => p.split(".").reduce((c, k) => (c && typeof c === "object" ? c[k] : undefined), o);
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
      {series.map(([path, title]) => {
        const pts = history.map((h) => Number(pick(h.data || {}, path))).filter((n) => Number.isFinite(n));
        const last = pts[pts.length - 1];
        const max = Math.max(...pts, 1);
        const min = Math.min(...pts, 0);
        return (
          <div key={path} className="border border-white/10 p-3" data-testid={`trend-${path}`}>
            <div className="label-tiny text-[9px] text-text-secondary">{title}</div>
            <div className="font-heading text-xl text-brand-accent tabular-nums">{last != null ? last : "—"}</div>
            {pts.length > 1 && (
              <div className="flex items-end gap-0.5 h-10 mt-2">
                {pts.map((v, i) => (
                  <div key={i} className="flex-1 bg-brand-accent/60" style={{ height: `${((v - min) / Math.max(max - min, 0.01)) * 100}%`, minHeight: "2px" }} />
                ))}
              </div>
            )}
            <div className="text-[10px] text-text-secondary mt-1">{pts.length} snapshot{pts.length === 1 ? "" : "s"}</div>
          </div>
        );
      })}
    </div>
  );
}

/* ----------------------- PROGRAMS ----------------------- */
function ProgramsSection({ id, current, previous, completions = [], clientId, reload }) {
  const templateNames = [...new Set([...current, ...previous].map((a) => a.program_name).filter(Boolean))];
  return (
    <Section id={id} title="Programs" icon={Barbell}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="label-tiny mb-2">// CURRENT ({current.length})</div>
          {current.length === 0 ? <EmptyRow text="No active program assigned." /> : (
            <div className="space-y-2" data-testid="programs-current">
              {current.map((a) => <ProgramRow key={a.assignment_id} a={a} live />)}
            </div>
          )}
        </div>
        <div>
          <div className="label-tiny mb-2">// PREVIOUS ({previous.length})</div>
          {previous.length === 0 ? <EmptyRow text="No history yet." /> : (
            <div className="space-y-2" data-testid="programs-previous">
              {previous.slice(0, 10).map((a) => <ProgramRow key={a.assignment_id} a={a} />)}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4">
        <div className="label-tiny mb-2">// TEMPLATES USED ({templateNames.length})</div>
        {templateNames.length === 0 ? <EmptyRow text="No templates used yet." /> : (
          <div className="flex flex-wrap gap-2" data-testid="programs-templates">
            {templateNames.map((n) => (
              <span key={n} className="border border-white/10 px-3 py-1 text-xs">{n}</span>
            ))}
          </div>
        )}
      </div>
      <div className="mt-6 border-t border-white/10 pt-4">
        <div className="label-tiny mb-2">// COMPLETED SESSIONS · LEAVE FEEDBACK ({completions.length})</div>
        {completions.length === 0 ? <EmptyRow text="No completed sessions yet." /> : (
          <div className="space-y-2" data-testid="completions-feedback-list">
            {completions.slice(0, 15).map((c) => (
              <SessionFeedbackRow key={c.completion_id} c={c} clientId={clientId} reload={reload} />
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}

function SessionFeedbackRow({ c, clientId, reload }) {
  const [text, setText] = useState(c.coach_feedback || "");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const save = async () => {
    const fb = (text || "").trim();
    if (!fb) { toast.error("Enter feedback first"); return; }
    setSaving(true);
    try {
      await api.post(`/coach/clients/${clientId}/completions/${c.completion_id}/feedback`, { feedback: fb });
      toast.success("Feedback sent to athlete");
      setOpen(false);
      reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save feedback");
    } finally { setSaving(false); }
  };
  return (
    <div className="border border-white/10 p-3" data-testid={`completion-row-${c.completion_id}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-heading text-sm truncate">{c.session_name || `W${c.week}·D${c.day}`}</div>
          <div className="text-[11px] text-text-secondary">
            W{c.week}·D{c.day}{c.duration_min ? ` · ${c.duration_min} min` : ""}{c.new_prs ? ` · ${c.new_prs} PR` : ""}
            {c.completed_at ? ` · ${new Date(c.completed_at).toLocaleDateString()}` : ""}
          </div>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)}
          className="label-tiny text-[10px] border border-white/15 px-2 py-1 hover:border-brand-accent hover:text-brand-accent transition-colors"
          data-testid={`completion-feedback-toggle-${c.completion_id}`}>
          {c.coach_feedback ? "EDIT FEEDBACK" : "ADD FEEDBACK"}
        </button>
      </div>
      {c.coach_feedback && !open && (
        <div className="mt-2 text-xs text-white/80 border-l-2 border-yellow-400/50 pl-2" data-testid={`completion-feedback-text-${c.completion_id}`}>
          {c.coach_feedback}
        </div>
      )}
      {(c.exercises || []).filter((e) => (e.mode || "").toLowerCase() === "cardio").length > 0 && (
        <div className="mt-2 space-y-1" data-testid={`completion-cardio-${c.completion_id}`}>
          {(c.exercises || []).filter((e) => (e.mode || "").toLowerCase() === "cardio").map((e, i) => {
            const ivs = e.intervals || [];
            let dist = e.result_distance, unit = e.distance_unit || "m", time = e.result_time, count = 0;
            if (ivs.length) {
              count = ivs.length;
              unit = ivs[0].distance_unit || "m";
              dist = +ivs.reduce((s, iv) => s + (parseFloat(iv.distance) || 0), 0).toFixed(1);
              const totS = ivs.reduce((s, iv) => s + (parseClock(iv.time) || 0), 0);
              time = totS ? fmtClock(totS) : null;
            }
            const pace = formatPace(time, dist, unit);
            return (
              <div key={i} className="text-[11px] text-text-secondary flex items-center gap-2 flex-wrap">
                <span className="text-white">{e.name}</span>
                {count ? <span className="tabular-nums">{count}×</span> : null}
                {dist ? <span className="tabular-nums">{dist} {unit}</span> : null}
                {time ? <span className="tabular-nums">· {time}</span> : null}
                {e.result_load ? <span className="tabular-nums">· {e.result_load} kg</span> : null}
                {pace ? <span className="text-brand-accent tabular-nums label-tiny text-[10px]">· {count ? "avg " : ""}{pace}</span> : null}
              </div>
            );
          })}
        </div>
      )}
      {open && (
        <div className="mt-2">
          <textarea
            value={text} onChange={(e) => setText(e.target.value)} rows={3}
            placeholder="Feedback on this session — the athlete will see it on their session."
            className="w-full bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
            data-testid={`completion-feedback-input-${c.completion_id}`}
          />
          <button type="button" onClick={save} disabled={saving}
            className="btn-kinetic mt-2 px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50"
            data-testid={`completion-feedback-save-${c.completion_id}`}>
            {saving ? "SENDING…" : "SEND FEEDBACK"}
          </button>
        </div>
      )}
    </div>
  );
}

function ProgramRow({ a, live }) {
  return (
    <div className={`border p-3 ${live ? "border-brand-accent/40 bg-brand-accent/[0.03]" : "border-white/10"}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading text-sm">{a.program_name}</div>
          <div className="text-xs text-text-secondary mt-1">
            Started {fmtDate(a.start_date)} · {a.program_meta?.weeks || a.total_weeks || "?"} wk · {a.program_meta?.sessions || "?"} sessions
            {a.assigned_via_team_id ? " · via team" : ""}
          </div>
        </div>
        <span className={`text-[10px] label-tiny ${live ? "text-brand-accent" : "text-text-secondary"}`}>{(a.status || "").toUpperCase()}</span>
      </div>
    </div>
  );
}

/* ----------------------- RECOVERY ----------------------- */
function RecoverySection({ id, recovery, sleep, assessment, latest }) {
  const rm = assessment?.recovery_metrics || {};
  const sleepAvg = useMemo(() => {
    const vals = sleep.map((s) => Number(s.duration_hours ?? s.hours ?? s.duration_min ? s.duration_min / 60 : NaN)).filter((n) => Number.isFinite(n));
    if (!vals.length) return null;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  }, [sleep]);

  return (
    <Section id={id} title="Recovery" icon={Heart}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="RECOVERY SCORE" value={latest?.recovery_score ?? "—"} color="orange" data-testid="rec-score" />
        <Card label="HRV" value={rm.hrv_ms || latest?.hrv_ms || "—"} hint="ms" data-testid="rec-hrv" />
        <Card label="RESTING HR" value={rm.resting_hr || latest?.resting_hr || "—"} hint="bpm" data-testid="rec-hr" />
        <Card label="STRESS" value={rm.stress_level || latest?.stress_level || "—"} data-testid="rec-stress" />
        <Card label="SLEEP AVG (14D)" value={sleepAvg ? `${sleepAvg} h` : "—"} data-testid="rec-sleep-avg" />
        <Card label="READINESS" value={rm.readiness || latest?.readiness || "—"} data-testid="rec-readiness" />
        <Card label="LOGS (30D)" value={recovery.length} hint="recovery entries" data-testid="rec-log-count" />
        <Card label="SLEEP LOGS (30D)" value={sleep.length} hint="entries" data-testid="rec-sleep-count" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="label-tiny mb-2">// RECENT RECOVERY LOGS</div>
          {recovery.length === 0 ? <EmptyRow text="No recovery logs." /> : (
            <div className="border border-white/10 divide-y divide-white/5 max-h-56 overflow-y-auto" data-testid="rec-log-list">
              {recovery.slice(0, 15).map((r, i) => (
                <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-text-secondary tabular-nums">{fmtDate(r.date)}</span>
                  <span className="tabular-nums">HRV {r.hrv_ms || "—"} · HR {r.resting_hr || "—"} · Score {r.recovery_score ?? "—"}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="label-tiny mb-2">// SLEEP LOGS</div>
          {sleep.length === 0 ? <EmptyRow text="No sleep logs." /> : (
            <div className="border border-white/10 divide-y divide-white/5 max-h-56 overflow-y-auto" data-testid="rec-sleep-list">
              {sleep.slice(0, 15).map((s, i) => {
                const hrs = Number(s.duration_hours ?? s.hours ?? (s.duration_min ? s.duration_min / 60 : null));
                return (
                  <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                    <span className="text-text-secondary tabular-nums">{fmtDate(s.date)}</span>
                    <span className="tabular-nums">{Number.isFinite(hrs) ? `${hrs.toFixed(1)} h` : "—"}{s.quality ? ` · ${s.quality}/10` : ""}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

/* ----------------------- DAILY CHECK-INS ----------------------- */
const _ciParse = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const _ciFmt = (dt) => dt.toISOString().slice(0, 10);
const CI_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function CheckinsSection({ id, checkins, clientName }) {
  const hmRef = useRef(null);
  useEffect(() => { if (hmRef.current) hmRef.current.scrollLeft = hmRef.current.scrollWidth; }, [checkins]);

  if (!checkins) {
    return <Section id={id} title="Daily Check-ins" icon={Fire}><EmptyRow text="No check-in data yet." /></Section>;
  }

  const logged = new Set(checkins.logged_dates || []);
  const trained = new Set(checkins.trained_dates || []);
  const toYmd = checkins.to;
  const from = _ciParse(checkins.from);
  const to = _ciParse(checkins.to);
  const gridStart = new Date(from);
  gridStart.setUTCDate(from.getUTCDate() - from.getUTCDay());
  const days = [];
  for (let cur = new Date(gridStart); cur <= to; cur.setUTCDate(cur.getUTCDate() + 1)) days.push(_ciFmt(cur));
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  let lastMonth = -1;
  const monthLabels = weeks.map((w) => {
    const mo = _ciParse(w[0]).getUTCMonth();
    if (mo !== lastMonth) { lastMonth = mo; return CI_MONTHS[mo]; }
    return "";
  });
  const cellClass = (d) => {
    if (!d || d > toYmd) return "bg-transparent";
    if (logged.has(d) && trained.has(d)) return "bg-brand-accent";
    if (logged.has(d)) return "bg-brand-accent/40";
    return "bg-white/[0.07]";
  };

  return (
    <Section id={id} title="Daily Check-ins" icon={Fire}>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card label="CURRENT STREAK" value={checkins.streak} hint="days" color="orange" data-testid="ci-streak" />
        <Card label="BEST STREAK" value={checkins.longest} hint="days" data-testid="ci-longest" />
        <Card label="THIS MONTH" value={checkins.this_month} hint="logged" data-testid="ci-month" />
        <Card label="ALL-TIME" value={checkins.total_logged} hint="check-ins" data-testid="ci-total" />
        <Card label="TODAY" value={checkins.done_today ? "✓" : "—"} hint={checkins.done_today ? "logged" : "not yet"} data-testid="ci-today" />
      </div>

      <div className="mt-5">
        <div className="label-tiny mb-2">// LAST 90 DAYS</div>
        <div ref={hmRef} className="overflow-x-auto pb-2" data-testid="ci-heatmap">
          <div className="inline-flex flex-col gap-1 min-w-max">
            <div className="flex gap-1 pl-7">
              {monthLabels.map((m, i) => <div key={i} className="w-3.5 text-[9px] text-text-secondary">{m}</div>)}
            </div>
            <div className="flex">
              <div className="flex flex-col gap-1 pr-1 justify-between text-[9px] text-text-secondary">
                {["", "Mon", "", "Wed", "", "Fri", ""].map((d, i) => <div key={i} className="h-3.5">{d}</div>)}
              </div>
              <div className="flex gap-1">
                {weeks.map((w, wi) => (
                  <div key={wi} className="flex flex-col gap-1">
                    {Array.from({ length: 7 }).map((_, di) => (
                      <div key={di} title={w[di] || ""} className={`w-3.5 h-3.5 rounded-sm ${cellClass(w[di])}`} />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-2 text-[10px] text-text-secondary label-tiny">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-brand-accent" /> TRAINED</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-brand-accent/40" /> REST-DAY LOG</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-white/[0.07]" /> MISSED</span>
        </div>
      </div>

      <div className="mt-5">
        <div className="label-tiny mb-2">// RECENT CHECK-INS</div>
        {(!checkins.recent || checkins.recent.length === 0) ? <EmptyRow text={`${clientName || "This athlete"} hasn't logged a check-in yet.`} /> : (
          <div className="border border-white/10 divide-y divide-white/5 max-h-80 overflow-y-auto" data-testid="ci-recent-list">
            {checkins.recent.map((r, i) => (
              <div key={i} className="px-3 py-2.5 text-sm" data-testid={`ci-recent-${r.date}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-text-secondary tabular-nums">{fmtDate(r.date)}</span>
                  <span className={`text-[10px] label-tiny px-2 py-0.5 rounded ${r.trained ? "bg-brand-accent/20 text-brand-accent" : "bg-white/10 text-text-secondary"}`}>
                    {r.trained ? `${r.workout_type || "Trained"}${r.workout_duration_min ? ` · ${r.workout_duration_min}m` : ""}` : "REST DAY"}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-text-secondary tabular-nums">
                  {r.mood != null && <span>Mood {r.mood}/5</span>}
                  {r.energy != null && <span>Energy {r.energy}/5</span>}
                  {r.soreness != null && <span>Soreness {r.soreness}/5</span>}
                  {r.sleep_hours != null && <span>Sleep {r.sleep_hours}h</span>}
                  {r.intensity != null && <span>RPE {r.intensity}/5</span>}
                </div>
                {r.note && <div className="text-xs text-text-secondary italic mt-1.5">"{r.note}"</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </Section>
  );
}


/* ----------------------- MEDICAL ----------------------- */
function MedicalSection({ id, clientId, assessment, medical, reload }) {
  const injuries = assessment?.general?.previous_injuries || "";
  const ms = assessment?.movement_screen || {};
  const flagged = Object.entries(ms).filter(([, m]) => m?.pain || m?.compensations);
  const [rArea, setRArea] = useState(""); const [rLevel, setRLevel] = useState("soft"); const [rNotes, setRNotes] = useState("");
  const [pArea, setPArea] = useState(""); const [pSev, setPSev] = useState(3); const [pNotes, setPNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [docKind, setDocKind] = useState("scan");
  const fileRef = React.useRef(null);

  const addRestriction = async () => {
    if (!rArea.trim()) return toast.error("Area required");
    try { await api.post(`/coach/clients/${clientId}/medical/restriction`, { area: rArea, level: rLevel, notes: rNotes });
      setRArea(""); setRNotes(""); toast.success("Restriction added"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeRestriction = async (rid) => { await api.delete(`/coach/clients/${clientId}/medical/restriction/${rid}`); reload(); };
  const addPain = async () => {
    if (!pArea.trim()) return toast.error("Area required");
    try { await api.post(`/coach/clients/${clientId}/medical/pain`, { area: pArea, severity: Number(pSev), notes: pNotes });
      setPArea(""); setPNotes(""); toast.success("Pain logged"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removePain = async (pid) => { await api.delete(`/coach/clients/${clientId}/medical/pain/${pid}`); reload(); };
  const uploadDoc = async () => {
    const f = fileRef.current?.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/coach/clients/${clientId}/medical/document?kind=${docKind}&name=${encodeURIComponent(f.name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const removeDoc = async (did) => { await api.delete(`/coach/clients/${clientId}/medical/document/${did}`); reload(); };

  return (
    <Section id={id} title="Medical" icon={Bandaids}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <div className="label-tiny mb-2">// PREVIOUS INJURIES · MOVEMENT FLAGS</div>
          {injuries ? <div className="border border-red-500/20 bg-red-500/[0.03] p-3 text-sm whitespace-pre-wrap mb-2" data-testid="med-injuries">{injuries}</div> : null}
          {flagged.length > 0 && (
            <div className="border border-white/10 divide-y divide-white/5" data-testid="med-flags">
              {flagged.map(([name, m]) => (
                <div key={name} className="px-3 py-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-heading">{name}</span>
                    {m.pain && <span className="text-[10px] label-tiny text-red-500 border border-red-500/40 px-1.5 py-0.5">PAIN</span>}
                  </div>
                  {m.compensations && <div className="text-xs text-text-secondary mt-1">Comp: {m.compensations}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <div className="label-tiny mb-2">// ACTIVE RESTRICTIONS ({medical.restrictions?.length || 0})</div>
          <div className="border border-white/10 mb-2" data-testid="med-restriction-list">
            {(medical.restrictions || []).length === 0 ? <EmptyRow text="No restrictions." /> :
              (medical.restrictions || []).map((r) => (
                <div key={r.id} className="flex items-start justify-between px-3 py-2 text-sm border-b border-white/5 last:border-0" data-testid={`med-restriction-${r.id}`}>
                  <div>
                    <div className="font-heading text-sm">{r.area} <span className="text-[10px] text-brand-accent ml-1">{(r.level || "").toUpperCase()}</span></div>
                    {r.notes && <div className="text-xs text-text-secondary">{r.notes}</div>}
                  </div>
                  <button onClick={() => removeRestriction(r.id)} data-testid={`med-restriction-${r.id}-remove`} className="text-text-secondary hover:text-red-500"><Trash size={12} /></button>
                </div>
              ))}
          </div>
          <div className="grid grid-cols-6 gap-1" data-testid="med-restriction-add">
            <Input value={rArea} onChange={(e) => setRArea(e.target.value)} placeholder="Area" className="col-span-2 h-8 text-xs bg-surface-elevated border-white/10" data-testid="med-restriction-area" />
            <select value={rLevel} onChange={(e) => setRLevel(e.target.value)} className="col-span-1 h-8 text-xs bg-surface-elevated border border-white/10 px-1" data-testid="med-restriction-level">
              <option value="soft">soft</option><option value="hard">hard</option><option value="return">return-to-play</option>
            </select>
            <Input value={rNotes} onChange={(e) => setRNotes(e.target.value)} placeholder="notes" className="col-span-2 h-8 text-xs bg-surface-elevated border-white/10" data-testid="med-restriction-notes" />
            <button onClick={addRestriction} className="col-span-1 bg-brand-accent text-surface-base text-[10px] label-tiny" data-testid="med-restriction-submit">ADD</button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <div>
          <div className="label-tiny mb-2">// PAIN LOG ({medical.pain_log?.length || 0})</div>
          <div className="border border-white/10 max-h-56 overflow-y-auto mb-2" data-testid="med-pain-list">
            {(medical.pain_log || []).length === 0 ? <EmptyRow text="No pain entries." /> :
              (medical.pain_log || []).map((p) => (
                <div key={p.id} className="flex items-center justify-between px-3 py-2 text-sm border-b border-white/5 last:border-0" data-testid={`med-pain-${p.id}`}>
                  <div>
                    <div className="text-xs text-text-secondary tabular-nums">{fmtDate(p.date)}</div>
                    <div>{p.area}{p.notes ? <span className="text-xs text-text-secondary"> · {p.notes}</span> : null}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs tabular-nums font-heading ${p.severity >= 6 ? "text-red-500" : p.severity >= 3 ? "text-brand-accent" : "text-green-500"}`}>{p.severity}/10</span>
                    <button onClick={() => removePain(p.id)} className="text-text-secondary hover:text-red-500"><Trash size={12} /></button>
                  </div>
                </div>
              ))}
          </div>
          <div className="grid grid-cols-6 gap-1" data-testid="med-pain-add">
            <Input value={pArea} onChange={(e) => setPArea(e.target.value)} placeholder="Area" className="col-span-2 h-8 text-xs bg-surface-elevated border-white/10" data-testid="med-pain-area" />
            <Input type="number" min="0" max="10" value={pSev} onChange={(e) => setPSev(e.target.value)} placeholder="0-10" className="col-span-1 h-8 text-xs bg-surface-elevated border-white/10" data-testid="med-pain-severity" />
            <Input value={pNotes} onChange={(e) => setPNotes(e.target.value)} placeholder="notes" className="col-span-2 h-8 text-xs bg-surface-elevated border-white/10" data-testid="med-pain-notes" />
            <button onClick={addPain} className="col-span-1 bg-brand-accent text-surface-base text-[10px] label-tiny" data-testid="med-pain-submit">LOG</button>
          </div>
        </div>
        <div>
          <div className="label-tiny mb-2">// SCANS · PHYSIO DOCS ({medical.documents?.length || 0})</div>
          <div className="border border-white/10 max-h-56 overflow-y-auto mb-2" data-testid="med-docs-list">
            {(medical.documents || []).length === 0 ? <EmptyRow text="No scans / physio notes." /> :
              (medical.documents || []).map((d) => (
                <div key={d.id} className="flex items-center justify-between px-3 py-2 text-sm border-b border-white/5 last:border-0" data-testid={`med-doc-${d.id}`}>
                  <div>
                    <a href={resolveMedia(d.url)} target="_blank" rel="noreferrer" className="font-heading text-brand-accent hover:underline">{d.name}</a>
                    <div className="text-xs text-text-secondary tabular-nums">{d.kind.toUpperCase()} · {(d.size / 1024).toFixed(0)} KB · {fmtDate(d.uploaded_at)}</div>
                  </div>
                  <button onClick={() => removeDoc(d.id)} className="text-text-secondary hover:text-red-500"><Trash size={12} /></button>
                </div>
              ))}
          </div>
          <div className="flex items-center gap-1">
            <select value={docKind} onChange={(e) => setDocKind(e.target.value)} className="h-8 text-xs bg-surface-elevated border border-white/10 px-2" data-testid="med-doc-kind">
              <option value="scan">SCAN</option><option value="physio">PHYSIO</option>
            </select>
            <label className="flex-1 inline-flex items-center gap-1 border border-white/20 px-2 h-8 text-xs label-tiny cursor-pointer hover:border-brand-accent" data-testid="med-doc-upload-label">
              <UploadSimple size={11} /> {uploading ? "UPLOADING..." : "UPLOAD"}
              <input ref={fileRef} type="file" accept="image/*,application/pdf" onChange={uploadDoc} className="hidden" data-testid="med-doc-upload-input" />
            </label>
          </div>
        </div>
      </div>
    </Section>
  );
}

/* ----------------------- COMPETITIONS ----------------------- */
const PRIORITY_STYLE = {
  A: { chip: "bg-red-100 text-red-800 border border-red-300", label: "A · PRIMARY" },
  B: { chip: "bg-amber-100 text-amber-900 border border-amber-300", label: "B · IMPORTANT" },
  C: { chip: "bg-slate-100 text-slate-700 border border-slate-300", label: "C · TUNE-UP" },
};

function PriorityChip({ p }) {
  if (!p) return null;
  const s = PRIORITY_STYLE[p] || PRIORITY_STYLE.C;
  return <span className={`label-tiny text-[9px] px-1.5 py-0.5 ${s.chip}`} data-testid={`comp-priority-${p}`}>{s.label}</span>;
}

function CountdownPill({ days }) {
  if (days == null) return null;
  const negative = days < 0;
  const cls = negative
    ? "bg-slate-100 text-slate-600 border border-slate-200"
    : days <= 7  ? "bg-red-100 text-red-800 border border-red-300"
    : days <= 30 ? "bg-amber-100 text-amber-900 border border-amber-300"
    :              "bg-blue-100 text-blue-800 border border-blue-300";
  return (
    <span className={`label-tiny text-[10px] px-2 py-0.5 tabular-nums ${cls}`} data-testid="comp-countdown">
      {negative ? `${Math.abs(days)}D AGO` : days === 0 ? "TODAY" : `IN ${days}D`}
    </span>
  );
}

function TravelBlock({ travel }) {
  if (!travel) return null;
  const anyField = travel.departure_date || travel.return_date || travel.location || travel.flight_ref || travel.hotel || travel.notes;
  if (!anyField) return null;
  return (
    <div className="mt-2 border border-slate-200 bg-slate-50 p-2" data-testid="comp-travel">
      <div className="label-tiny text-[9px] text-slate-500 mb-1">// TRAVEL</div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
        {travel.location && <div className="col-span-2"><span className="text-slate-500">Location</span> <span className="text-slate-800">{travel.location}</span></div>}
        {travel.departure_date && <div><span className="text-slate-500">Depart</span> <span className="text-slate-800 tabular-nums">{fmtDate(travel.departure_date)}</span></div>}
        {travel.return_date && <div><span className="text-slate-500">Return</span> <span className="text-slate-800 tabular-nums">{fmtDate(travel.return_date)}</span></div>}
        {travel.flight_ref && <div><span className="text-slate-500">Flight</span> <span className="text-slate-800">{travel.flight_ref}</span></div>}
        {travel.hotel && <div><span className="text-slate-500">Hotel</span> <span className="text-slate-800">{travel.hotel}</span></div>}
        {travel.notes && <div className="col-span-2 text-slate-700 whitespace-pre-wrap">{travel.notes}</div>}
      </div>
    </div>
  );
}

function CompetitionRow({ c, onRemove, taperProgramName }) {
  const days = daysUntil(c.date);
  return (
    <div className={`border p-3 ${c.status === "past" ? "border-white/10" : "border-brand-accent/40 bg-brand-accent/[0.03]"}`} data-testid={`comp-${c.competition_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading text-sm">{c.name}{c.event ? ` · ${c.event}` : ""}</div>
            <PriorityChip p={c.priority} />
            {c.status !== "past" && <CountdownPill days={days} />}
          </div>
          <div className={`text-xs mt-1 tabular-nums ${c.status === "past" ? "text-text-secondary" : "text-brand-accent"}`}>
            {fmtDate(c.date)}
          </div>
          {(c.weight_class || c.division) && (
            <div className="text-[11px] text-text-secondary mt-0.5">
              {c.weight_class && <><span>Weight</span> <span className="text-white/90">{c.weight_class}</span></>}
              {c.weight_class && c.division && <span> · </span>}
              {c.division && <><span>Division</span> <span className="text-white/90">{c.division}</span></>}
            </div>
          )}
          {c.place != null && <div className="text-xs mt-0.5"><span className="text-text-secondary">Place</span> <span className="font-heading text-brand-accent">#{c.place}</span></div>}
          {c.result_value && <div className="text-xs mt-0.5"><span className="text-text-secondary">Result</span> <span className="font-heading">{c.result_value}{c.result_unit ? ` ${c.result_unit}` : ""}</span></div>}
          {c.notes && <div className="text-xs text-text-secondary mt-1 whitespace-pre-wrap">{c.notes}</div>}
          <TravelBlock travel={c.travel} />
          {c.taper_program_id && (
            <Link
              to={`/coach/programs?open=${c.taper_program_id}`}
              className="mt-2 inline-flex items-center gap-1 text-[11px] label-tiny tracking-wider text-brand-accent hover:text-white transition border border-brand-accent/40 hover:border-brand-accent px-2 py-1"
              data-testid={`comp-taper-link-${c.competition_id}`}
            >
              <Barbell size={11} /> OPEN TAPER{taperProgramName ? ` · ${taperProgramName}` : ""}
            </Link>
          )}
          {c.video_url && <div className="mt-1"><a href={resolveMedia(c.video_url)} target="_blank" rel="noreferrer" className="text-xs text-brand-accent hover:underline">▶ VIDEO</a></div>}
        </div>
        <button onClick={() => onRemove(c.competition_id)} className="text-text-secondary hover:text-red-500" data-testid={`comp-delete-${c.competition_id}`}><Trash size={12} /></button>
      </div>
    </div>
  );
}

function MatchDaysCard({ clientId }) {
  const [days, setDays] = useState([]);
  const [tz, setTz] = useState("UTC");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDate, setNewDate] = useState("");

  useEffect(() => {
    let alive = true;
    api.get(`/coach/clients/${clientId}/match-days`).then(({ data }) => {
      if (!alive) return;
      setDays(data?.match_days || []);
      setTz(data?.timezone || "UTC");
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [clientId]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/coach/clients/${clientId}/match-days`, { match_days: next });
      setDays(data?.match_days || []);
      toast.success("Match days updated");
    } catch {
      toast.error("Could not save match days");
    } finally {
      setSaving(false);
    }
  };

  const addDate = () => {
    if (!newDate) return;
    if (days.includes(newDate)) { toast.info("Already added"); setNewDate(""); return; }
    persist([...days, newDate].sort());
    setNewDate("");
  };

  const removeDate = (d) => persist(days.filter((x) => x !== d));

  return (
    <div className="border border-brand-heat/30 bg-brand-heat/[0.04] p-4 mb-4" data-testid="coach-match-days-card">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="label-tiny text-brand-heat text-[10px]">// MATCH DAYS · OVERRIDE</div>
          <div className="font-heading text-sm">Push always fires on these days</div>
        </div>
        <div className="label-tiny text-[10px] text-text-secondary">TZ: {tz}</div>
      </div>
      <p className="text-[11px] text-text-secondary mb-3">
        Bypasses the athlete&rsquo;s quiet hours and Sunday-off so no coach ping is missed on game day. The athlete is notified of your changes.
      </p>
      <div className="flex gap-2 items-center">
        <Input
          type="date"
          value={newDate}
          min={new Date().toISOString().slice(0, 10)}
          onChange={(e) => setNewDate(e.target.value)}
          className="h-8 text-xs bg-surface-elevated border-white/10 max-w-[180px]"
          data-testid="coach-match-day-input"
          disabled={loading || saving}
        />
        <button
          type="button"
          onClick={addDate}
          disabled={!newDate || saving}
          className="px-3 py-1.5 h-8 border border-brand-heat text-brand-heat text-[11px] label-tiny tracking-widest hover:!bg-brand-heat hover:!text-white transition-colors disabled:opacity-50"
          data-testid="coach-match-day-add"
        >
          ADD
        </button>
      </div>
      {days.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="coach-match-days-list">
          {days.map((d) => (
            <span
              key={d}
              className="inline-flex items-center gap-1.5 border border-brand-heat/50 bg-brand-heat/10 text-brand-heat px-2 py-1 text-[11px] label-tiny tracking-wider"
              data-testid={`coach-match-day-${d}`}
            >
              {new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              <button
                type="button"
                onClick={() => removeDate(d)}
                disabled={saving}
                className="hover:!text-white transition-colors disabled:opacity-50"
                aria-label={`Remove ${d}`}
                data-testid={`coach-match-day-remove-${d}`}
              >
                <X size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CompetitionsSection({ id, clientId, competitions, reload }) {
  // Basic fields
  const [name, setName] = useState(""); const [date, setDate] = useState("");
  const [event, setEvent] = useState(""); const [place, setPlace] = useState("");
  const [result, setResult] = useState(""); const [notes, setNotes] = useState("");
  const [video, setVideo] = useState("");
  // M7 additions
  const [priority, setPriority] = useState("");
  const [weightClass, setWeightClass] = useState(""); const [division, setDivision] = useState("");
  const [taperProgramId, setTaperProgramId] = useState("");
  const [showTravel, setShowTravel] = useState(false);
  const [travel, setTravel] = useState({ departure_date: "", return_date: "", location: "", flight_ref: "", hotel: "", notes: "" });
  const [programs, setPrograms] = useState([]);

  useEffect(() => {
    api.get("/coach/programs").then(({ data }) => setPrograms(data || [])).catch(() => {});
  }, []);
  const programMap = useMemo(() => Object.fromEntries((programs || []).map((p) => [p.program_id, p.name])), [programs]);

  const upcoming = competitions.filter((c) => c.status !== "past");
  const past = competitions.filter((c) => c.status === "past");

  const resetForm = () => {
    setName(""); setEvent(""); setPlace(""); setResult(""); setNotes(""); setVideo("");
    setPriority(""); setWeightClass(""); setDivision(""); setTaperProgramId("");
    setTravel({ departure_date: "", return_date: "", location: "", flight_ref: "", hotel: "", notes: "" });
    setShowTravel(false);
  };
  const add = async () => {
    if (!name.trim() || !date) return toast.error("Name + date required");
    const travelHasData = Object.values(travel).some((v) => v && String(v).trim());
    const payload = {
      name, date,
      event: event || null,
      place: place ? Number(place) : null,
      result_value: result || null,
      notes: notes || null,
      video_url: video || null,
      priority: priority || null,
      weight_class: weightClass || null,
      division: division || null,
      taper_program_id: taperProgramId || null,
      travel: travelHasData ? travel : null,
    };
    try {
      await api.post(`/coach/clients/${clientId}/competitions`, payload);
      resetForm();
      toast.success("Competition added"); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (cid) => { if (!window.confirm("Delete competition?")) return; await api.delete(`/coach/clients/${clientId}/competitions/${cid}`); reload(); };

  return (
    <Section id={id} title="Competitions" icon={Trophy}>
      <MatchDaysCard clientId={clientId} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="label-tiny mb-2">// UPCOMING ({upcoming.length})</div>
          {upcoming.length === 0 ? <EmptyRow text="No upcoming competitions." /> :
            <div className="space-y-2" data-testid="comp-upcoming">
              {upcoming.map((c) => <CompetitionRow key={c.competition_id} c={c} onRemove={remove} taperProgramName={programMap[c.taper_program_id]} />)}
            </div>}
        </div>
        <div>
          <div className="label-tiny mb-2">// PAST ({past.length})</div>
          {past.length === 0 ? <EmptyRow text="No past competitions." /> :
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="comp-past">
              {past.map((c) => <CompetitionRow key={c.competition_id} c={c} onRemove={remove} taperProgramName={programMap[c.taper_program_id]} />)}
            </div>}
        </div>
      </div>

      <div className="border border-dashed border-white/10 p-3 space-y-3" data-testid="comp-add">
        <div className="label-tiny">// ADD COMPETITION</div>

        {/* Row 1 — core fields */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Meet name" className="col-span-2 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-name" />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="col-span-1 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-date" />
          <Input value={event} onChange={(e) => setEvent(e.target.value)} placeholder="Event" className="col-span-1 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-event" />
          <Input type="number" value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Place" className="col-span-1 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-place" />
          <Input value={result} onChange={(e) => setResult(e.target.value)} placeholder="Result" className="col-span-1 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-result" />
        </div>

        {/* Row 2 — priority + weight/division + taper */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="label-tiny text-[10px] text-text-secondary">PRIORITY</div>
          {["A", "B", "C"].map((p) => {
            const s = PRIORITY_STYLE[p];
            const active = priority === p;
            return (
              <button
                key={p}
                onClick={() => setPriority(active ? "" : p)}
                data-testid={`comp-add-priority-${p}`}
                className={`text-[10px] label-tiny tracking-wider px-2 py-1 transition ${
                  active ? s.chip : "bg-white text-slate-600 border border-slate-300 hover:border-slate-400"
                }`}
              >
                {p}
              </button>
            );
          })}
          <Input value={weightClass} onChange={(e) => setWeightClass(e.target.value)} placeholder="Weight class" className="h-8 text-xs bg-surface-elevated border-white/10 w-40" data-testid="comp-add-weight-class" />
          <Input value={division} onChange={(e) => setDivision(e.target.value)} placeholder="Division" className="h-8 text-xs bg-surface-elevated border-white/10 w-32" data-testid="comp-add-division" />
          <select
            value={taperProgramId}
            onChange={(e) => setTaperProgramId(e.target.value)}
            data-testid="comp-add-taper"
            className="h-8 text-xs bg-surface-elevated border border-white/10 text-white/90 px-2 flex-1 min-w-[180px]"
          >
            <option value="">— Link taper program (optional) —</option>
            {programs.map((p) => (
              <option key={p.program_id} value={p.program_id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Row 3 — travel toggle */}
        <div>
          <button
            onClick={() => setShowTravel((v) => !v)}
            className="text-[10px] label-tiny tracking-wider px-2 py-1 border border-slate-300 text-slate-700 hover:border-brand-accent hover:text-brand-accent transition"
            data-testid="comp-add-travel-toggle"
          >
            {showTravel ? "− TRAVEL DETAILS" : "+ TRAVEL DETAILS"}
          </button>
          {showTravel && (
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="comp-add-travel-block">
              <Input value={travel.location} onChange={(e) => setTravel({ ...travel, location: e.target.value })} placeholder="Location (city / venue)" className="col-span-2 md:col-span-2 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-travel-location" />
              <Input type="date" value={travel.departure_date} onChange={(e) => setTravel({ ...travel, departure_date: e.target.value })} placeholder="Depart" className="h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-travel-depart" />
              <Input type="date" value={travel.return_date} onChange={(e) => setTravel({ ...travel, return_date: e.target.value })} placeholder="Return" className="h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-travel-return" />
              <Input value={travel.flight_ref} onChange={(e) => setTravel({ ...travel, flight_ref: e.target.value })} placeholder="Flight ref" className="h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-travel-flight" />
              <Input value={travel.hotel} onChange={(e) => setTravel({ ...travel, hotel: e.target.value })} placeholder="Hotel" className="col-span-2 md:col-span-3 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-travel-hotel" />
              <Input value={travel.notes} onChange={(e) => setTravel({ ...travel, notes: e.target.value })} placeholder="Travel notes" className="col-span-2 md:col-span-4 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-travel-notes" />
            </div>
          )}
        </div>

        {/* Row 4 — video + notes */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <Input value={video} onChange={(e) => setVideo(e.target.value)} placeholder="Video URL" className="col-span-3 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-video" />
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes" className="col-span-3 h-8 text-xs bg-surface-elevated border-white/10" data-testid="comp-add-notes" />
        </div>

        <button onClick={add} className="bg-brand-accent text-surface-base py-1.5 px-4 text-xs label-tiny tracking-wider" data-testid="comp-add-submit">+ ADD COMPETITION</button>
      </div>
    </Section>
  );
}

/* ----------------------- DOCUMENTS ----------------------- */
function DocumentsSection({ id, clientId, documents, reload }) {
  const kinds = ["medical", "consent", "reports", "nutrition", "contracts"];
  const grouped = kinds.map((k) => [k, documents.filter((d) => d.kind === k)]);
  const [uploadingKind, setUploadingKind] = useState(null);
  const refs = React.useRef({});

  const trigger = (k) => refs.current[k]?.click();
  const upload = async (k, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingKind(k);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/coach/clients/${clientId}/documents?kind=${k}&name=${encodeURIComponent(f.name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingKind(null); if (refs.current[k]) refs.current[k].value = ""; }
  };
  const remove = async (did) => { await api.delete(`/coach/clients/${clientId}/documents/${did}`); reload(); };

  return (
    <Section id={id} title="Documents" icon={FileText}>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="doc-kinds">
        {grouped.map(([k, rows]) => (
          <div key={k} className="border border-white/10 p-3" data-testid={`doc-${k}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="label-tiny text-[9px]">{k.toUpperCase()}</div>
              <span className="text-[10px] text-text-secondary tabular-nums">{rows.length}</span>
            </div>
            <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
              {rows.length === 0 ? <div className="text-[10px] text-text-secondary italic">No files</div> :
                rows.map((d) => (
                  <div key={d.document_id} className="flex items-center justify-between gap-1 border border-white/5 px-2 py-1 text-[11px]" data-testid={`doc-${k}-${d.document_id}`}>
                    <a href={resolveMedia(d.url)} target="_blank" rel="noreferrer" className="truncate flex-1 text-brand-accent hover:underline">{d.name}</a>
                    <button onClick={() => remove(d.document_id)} className="text-text-secondary hover:text-red-500" data-testid={`doc-${k}-${d.document_id}-remove`}><Trash size={10} /></button>
                  </div>
                ))}
            </div>
            <button onClick={() => trigger(k)} disabled={uploadingKind === k} data-testid={`doc-${k}-upload`}
              className="w-full inline-flex items-center justify-center gap-1 py-1.5 text-[10px] label-tiny tracking-wider border border-white/15 hover:border-brand-accent hover:text-brand-accent transition disabled:opacity-60">
              <UploadSimple size={10} /> {uploadingKind === k ? "UPLOADING..." : "UPLOAD"}
            </button>
            <input ref={(el) => (refs.current[k] = el)} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => upload(k, e)} data-testid={`doc-${k}-upload-input`} />
          </div>
        ))}
      </div>
    </Section>
  );
}

/* ----------------------- MEDIA ----------------------- */
function MediaSection({ id, clientId, formReviews, media, reload }) {
  const kinds = [["technique", "TECHNIQUE VIDEOS"], ["coach", "COACH VIDEOS"], ["comparison", "COMPARISON VIDEOS"]];
  const refs = React.useRef({});
  const [uploadingKind, setUploadingKind] = useState(null);

  const trigger = (k) => refs.current[k]?.click();
  const upload = async (k, e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingKind(k);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post(`/coach/clients/${clientId}/media?kind=${k}&exercise=${encodeURIComponent(f.name)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingKind(null); if (refs.current[k]) refs.current[k].value = ""; }
  };
  const remove = async (mid) => { await api.delete(`/coach/clients/${clientId}/media/${mid}`); reload(); };

  return (
    <Section id={id} title="Media" icon={VideoCamera}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {kinds.map(([k, label]) => {
          const rows = media.filter((m) => m.kind === k);
          return (
            <div key={k} className="border border-white/10 p-3" data-testid={`media-${k}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="label-tiny text-[9px]">{label}</div>
                <span className="text-[10px] text-text-secondary tabular-nums">{rows.length}</span>
              </div>
              <div className="space-y-1 mb-2 max-h-40 overflow-y-auto">
                {rows.length === 0 ? <div className="text-[10px] text-text-secondary italic">No videos</div> :
                  rows.map((m) => (
                    <div key={m.media_id} className="flex items-center justify-between gap-1 border border-white/5 px-2 py-1 text-[11px]" data-testid={`media-${k}-${m.media_id}`}>
                      <a href={resolveMedia(m.url)} target="_blank" rel="noreferrer" className="truncate flex-1 text-brand-accent hover:underline">{m.exercise || "clip"}</a>
                      <button onClick={() => remove(m.media_id)} className="text-text-secondary hover:text-red-500"><Trash size={10} /></button>
                    </div>
                  ))}
              </div>
              <button onClick={() => trigger(k)} disabled={uploadingKind === k} data-testid={`media-${k}-upload`}
                className="w-full inline-flex items-center justify-center gap-1 py-1.5 text-[10px] label-tiny tracking-wider border border-white/15 hover:border-brand-accent hover:text-brand-accent transition disabled:opacity-60">
                <UploadSimple size={10} /> {uploadingKind === k ? "UPLOADING..." : "UPLOAD"}
              </button>
              <input ref={(el) => (refs.current[k] = el)} type="file" accept="video/*,image/*" className="hidden" onChange={(e) => upload(k, e)} data-testid={`media-${k}-upload-input`} />
            </div>
          );
        })}
      </div>
      {formReviews?.length > 0 && (
        <div className="mt-4">
          <div className="label-tiny mb-2">// LINKED FORM REVIEWS ({formReviews.length})</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 max-h-32 overflow-y-auto">
            {formReviews.slice(0, 12).map((f) => (
              <div key={f.review_id} className="border border-white/10 p-2 text-xs">
                <div className="font-heading text-xs">{f.exercise || "Lift"}</div>
                <div className="text-[10px] text-text-secondary tabular-nums">{fmtDate(f.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Section>
  );
}

/* ----------------------- COACH NOTES JOURNAL ----------------------- */
function CoachNotesSection({ id, clientId, notes, setNotes, save, saving, linkId, currentUserId, journal, reload }) {
  const [kind, setKind] = useState("note");
  const [title, setTitle] = useState(""); const [body, setBody] = useState("");
  const [participants, setParticipants] = useState("");
  const [wins, setWins] = useState(""); const [focus, setFocus] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const kindIcon = { note: Note, meeting: Users, feedback: ChatCircleDots };
  const kindColor = { note: "text-brand-accent", meeting: "text-green-500", feedback: "text-yellow-400" };

  const add = async () => {
    if (!body.trim() && !title.trim()) return toast.error("Add a title or body");
    try {
      const payload = { kind, title, body, date };
      if (kind === "meeting") payload.participants = participants;
      if (kind === "feedback") { payload.feedback_wins = wins; payload.feedback_focus = focus; }
      await api.post(`/coach/clients/${clientId}/notes`, payload);
      setTitle(""); setBody(""); setParticipants(""); setWins(""); setFocus("");
      toast.success(`${kind} added`); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (nid) => { if (!window.confirm("Delete entry?")) return; await api.delete(`/coach/clients/${clientId}/notes/${nid}`); reload(); };

  return (
    <Section id={id} title="Coach Notes" icon={Note}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pinned + composer */}
        <div className="space-y-4">
          <div>
            <div className="label-tiny mb-2">// PINNED NOTES</div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              className="bg-surface-elevated border-white/10 min-h-[100px] mb-2"
              placeholder="Injuries, preferences, program adjustments..." data-testid="coach-notes-input" />
            <button onClick={save} disabled={saving} className="btn-kinetic w-full py-2 text-xs label-tiny tracking-widest disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="save-notes-button">
              <FloppyDisk size={12} weight="bold" /> {saving ? "SAVING..." : "SAVE PINNED"}
            </button>
          </div>
          <div className="border border-white/10 p-3" data-testid="journal-add">
            <div className="label-tiny mb-2">// NEW JOURNAL ENTRY</div>
            <div className="flex gap-1 mb-2" data-testid="journal-kind-picker">
              {["note", "meeting", "feedback"].map((k) => {
                const KI = kindIcon[k];
                return (
                  <button key={k} onClick={() => setKind(k)} data-testid={`journal-kind-${k}`}
                    className={`flex-1 inline-flex items-center justify-center gap-1 py-1.5 text-[10px] label-tiny tracking-wider border ${kind === k ? "bg-brand-accent text-surface-base border-brand-accent" : "border-white/15 text-text-secondary hover:border-white/40"}`}>
                    <KI size={10} /> {k.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="col-span-1 h-8 text-xs bg-surface-elevated border-white/10" data-testid="journal-date" />
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" className="col-span-2 h-8 text-xs bg-surface-elevated border-white/10" data-testid="journal-title" />
            </div>
            {kind === "meeting" && <Input value={participants} onChange={(e) => setParticipants(e.target.value)} placeholder="Participants" className="mb-2 h-8 text-xs bg-surface-elevated border-white/10" data-testid="journal-participants" />}
            {kind === "feedback" ? (
              <div className="space-y-2 mb-2">
                <Textarea rows={2} value={wins} onChange={(e) => setWins(e.target.value)} placeholder="What went well…" className="bg-surface-elevated border-green-500/30 text-xs" data-testid="journal-wins" />
                <Textarea rows={2} value={focus} onChange={(e) => setFocus(e.target.value)} placeholder="Focus areas…" className="bg-surface-elevated border-brand-accent/30 text-xs" data-testid="journal-focus" />
              </div>
            ) : (
              <Textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Body…" className="bg-surface-elevated border-white/10 mb-2 text-xs" data-testid="journal-body" />
            )}
            <button onClick={add} className="w-full bg-brand-accent text-surface-base py-1.5 text-[10px] label-tiny tracking-widest" data-testid="journal-submit">+ ADD ENTRY</button>
          </div>
        </div>
        {/* Chronological feed */}
        <div>
          <div className="label-tiny mb-2">// CHRONOLOGICAL ({journal.length})</div>
          {journal.length === 0 ? <EmptyRow text="No journal entries yet." /> : (
            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1" data-testid="journal-feed">
              {journal.map((n) => {
                const KI = kindIcon[n.kind] || Note;
                return (
                  <div key={n.note_id} className="border border-white/10 p-3" data-testid={`journal-entry-${n.note_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="flex items-center gap-1.5">
                        <KI size={12} className={kindColor[n.kind] || "text-text-secondary"} />
                        <span className={`label-tiny text-[10px] ${kindColor[n.kind]}`}>{(n.kind || "").toUpperCase()}</span>
                        <span className="text-[10px] text-text-secondary tabular-nums">{fmtDate(n.date || n.created_at)}</span>
                      </div>
                      <button onClick={() => remove(n.note_id)} className="text-text-secondary hover:text-red-500"><Trash size={11} /></button>
                    </div>
                    {n.title && <div className="font-heading text-sm mb-1">{n.title}</div>}
                    {n.participants && <div className="text-[11px] text-text-secondary mb-1">👥 {n.participants}</div>}
                    {n.body && <div className="text-xs whitespace-pre-wrap">{n.body}</div>}
                    {n.feedback_wins && <div className="text-xs mt-1"><span className="text-green-500 label-tiny text-[9px]">WINS</span> {n.feedback_wins}</div>}
                    {n.feedback_focus && <div className="text-xs mt-1"><span className="text-brand-accent label-tiny text-[9px]">FOCUS</span> {n.feedback_focus}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className="mt-4">
        <div className="label-tiny mb-2">// LIVE CHAT WITH ATHLETE</div>
        <MessagesThread linkId={linkId} currentUserId={currentUserId} height="240px" />
      </div>
    </Section>
  );
}


/* ----------------------- READINESS ----------------------- */
function ReadinessSection({ id, readiness }) {
  const r = readiness || {};
  const state = r.state || "unknown";
  const score = r.score;
  const stateColor = { high: "text-green-500", moderate: "text-brand-accent", low: "text-red-500", unknown: "text-text-secondary" }[state];
  const stateBg = { high: "border-green-500/40 bg-green-500/[0.04]", moderate: "border-brand-accent/40 bg-brand-accent/[0.04]", low: "border-red-500/40 bg-red-500/[0.06]", unknown: "border-white/10" }[state];
  const trend = r.trend || [];
  const latest = r.latest || {};

  const maxScore = 100;
  const w = 320, h = 60, pad = 4;

  return (
    <Section id={id} title="Readiness" icon={Heartbeat}>
      <div className={`border ${stateBg} p-4 mb-4`} data-testid="readiness-hero">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="label-tiny text-[9px] text-text-secondary">TODAY&apos;S READINESS</div>
            <div className={`font-heading text-5xl ${stateColor}`} data-testid="readiness-score">
              {score != null ? score : "—"}<span className="text-lg text-text-secondary ml-1">/100</span>
            </div>
            <div className={`text-xs label-tiny mt-1 ${stateColor}`} data-testid="readiness-state">
              {state === "unknown" ? "NO RECOVERY LOG YET" : state.toUpperCase()}
              {latest.date && <span className="text-text-secondary ml-2">· {fmtDate(latest.date)}</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 flex-1 min-w-[280px]">
            <MetricPill label="SLEEP" value={latest.sleep_hours != null ? `${Number(latest.sleep_hours).toFixed(1)}h` : "—"} tid="readiness-sleep" />
            <MetricPill label="SORENESS" value={latest.soreness != null ? `${latest.soreness}/10` : "—"} tid="readiness-soreness" tone={latest.soreness >= 7 ? "warn" : ""} />
            <MetricPill label="STRESS" value={latest.stress != null ? `${latest.stress}/10` : "—"} tid="readiness-stress" tone={latest.stress >= 7 ? "warn" : ""} />
            <MetricPill label="HRV" value={r.hrv_ms != null ? `${r.hrv_ms} ms` : "—"} tid="readiness-hrv" />
            <MetricPill
              label="PAIN"
              value={latest.pain_severity != null ? `${latest.pain_severity}/10${latest.pain_location ? ` · ${latest.pain_location}` : ""}` : "Not submitted"}
              tid="readiness-pain"
              tone={latest.pain_severity != null && latest.pain_severity >= 7 ? "warn" : ""}
            />
          </div>
        </div>
      </div>
      {latest.pain_notes && (
        <div className="mb-4 text-xs text-text-secondary border-l-2 border-red-400/40 pl-2" data-testid="readiness-pain-notes">
          Pain note: {latest.pain_notes}
        </div>
      )}

      {(r.alerts || []).length > 0 && (
        <div className="mb-4 space-y-1" data-testid="readiness-alerts">
          {r.alerts.map((a, i) => (
            <div key={i} className={`border ${a.level === "high" ? "border-red-500/40 bg-red-500/[0.05] text-red-400" : "border-brand-accent/40 bg-brand-accent/[0.04] text-brand-accent"} px-3 py-2 text-xs flex items-center gap-2`} data-testid={`readiness-alert-${a.code}`}>
              <Warning size={12} weight="fill" /> {a.message}
            </div>
          ))}
        </div>
      )}

      <div>
        <div className="label-tiny mb-2">// 14-DAY READINESS TREND ({trend.length})</div>
        {trend.length === 0 ? (
          <EmptyRow text="No recovery logs yet. Athlete needs to log recovery for readiness scoring." />
        ) : (
          <div className="border border-white/10 p-3" data-testid="readiness-trend">
            <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14" preserveAspectRatio="none">
              {trend.length > 1 && (
                <polyline
                  points={trend.map((p, i) => {
                    const x = pad + (i * (w - pad * 2)) / Math.max(trend.length - 1, 1);
                    const y = h - pad - ((p.score || 0) / maxScore) * (h - pad * 2);
                    return `${x},${y}`;
                  }).join(" ")}
                  fill="none" stroke="currentColor" strokeWidth="1.5" className="text-brand-accent"
                />
              )}
              {trend.map((p, i) => {
                const x = pad + (i * (w - pad * 2)) / Math.max(trend.length - 1, 1);
                const y = h - pad - ((p.score || 0) / maxScore) * (h - pad * 2);
                const cls = (p.score || 0) < 50 ? "fill-red-500" : (p.score || 0) < 75 ? "fill-brand-accent" : "fill-green-500";
                return <circle key={i} cx={x} cy={y} r="2.5" className={cls} />;
              })}
            </svg>
            <div className="flex justify-between text-[9px] text-text-secondary mt-1 tabular-nums">
              <span>{trend[0]?.date}</span>
              <span>{trend[trend.length - 1]?.date}</span>
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function MetricPill({ label, value, tid, tone }) {
  const border = tone === "warn" ? "border-red-500/40" : "border-white/10";
  const color = tone === "warn" ? "text-red-500" : "text-white";
  return (
    <div className={`border ${border} p-2 min-w-0`} data-testid={tid}>
      <div className="label-tiny text-[9px] text-text-secondary">{label}</div>
      <div className={`font-heading text-lg ${color} tabular-nums truncate`} title={typeof value === "string" ? value : undefined}>{value}</div>
    </div>
  );
}

/* ----------------------- TIMELINE ----------------------- */
const TIMELINE_KIND_META = {
  workout:      { label: "Workout",     icon: Barbell,       color: "text-blue-400",       border: "border-blue-400/40" },
  pr:           { label: "PR",          icon: Medal,         color: "text-yellow-400",     border: "border-yellow-400/40" },
  test:         { label: "Test",        icon: ChartLineUp,   color: "text-brand-accent",   border: "border-brand-accent/40" },
  note:         { label: "Note",        icon: Note,          color: "text-green-500",      border: "border-green-500/40" },
  competition:  { label: "Competition", icon: Trophy,        color: "text-purple-400",     border: "border-purple-400/40" },
  form_review:  { label: "Form Review", icon: VideoCamera,   color: "text-cyan-400",       border: "border-cyan-400/40" },
  pain:         { label: "Pain",        icon: Bandaids,      color: "text-red-500",        border: "border-red-500/40" },
  assignment:   { label: "Assignment",  icon: Lightning,     color: "text-orange-400",     border: "border-orange-400/40" },
  readiness:    { label: "Readiness",   icon: Pulse,         color: "text-teal-500",       border: "border-teal-400/40" },
  completion:   { label: "Session Done", icon: FloppyDisk,   color: "text-emerald-500",    border: "border-emerald-400/40" },
};

function TimelineSection({ id, timeline }) {
  const [activeKinds, setActiveKinds] = useState([]); // empty = all
  const events = timeline?.events || [];
  const counts = timeline?.counts || {};
  const allKinds = Object.keys(TIMELINE_KIND_META);

  const filtered = useMemo(() => {
    if (activeKinds.length === 0) return events;
    const set = new Set(activeKinds);
    return events.filter((e) => set.has(e.kind));
  }, [events, activeKinds]);

  const toggle = (k) => {
    setActiveKinds((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  return (
    <Section id={id} title="Timeline" icon={ClockCounterClockwise}>
      <div className="flex flex-wrap items-center gap-1 mb-4" data-testid="timeline-filters">
        <button
          onClick={() => setActiveKinds([])}
          data-testid="timeline-filter-all"
          className={`px-3 py-1 text-[10px] label-tiny tracking-wider border ${activeKinds.length === 0 ? "bg-brand-accent text-surface-base border-brand-accent" : "border-white/15 text-text-secondary hover:border-white/40"}`}
        >ALL ({events.length})</button>
        {allKinds.map((k) => {
          const m = TIMELINE_KIND_META[k];
          const n = counts[k] || 0;
          if (n === 0) return null;
          const on = activeKinds.includes(k);
          return (
            <button
              key={k}
              onClick={() => toggle(k)}
              data-testid={`timeline-filter-${k}`}
              className={`inline-flex items-center gap-1 px-3 py-1 text-[10px] label-tiny tracking-wider border ${on ? "bg-brand-accent text-surface-base border-brand-accent" : `${m.border} ${m.color} hover:opacity-80`}`}
            >
              <m.icon size={10} weight="fill" /> {m.label.toUpperCase()} ({n})
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <EmptyRow text="No activity yet." />
      ) : (
        <ol className="relative border-l border-white/10 ml-2 space-y-3" data-testid="timeline-feed">
          {filtered.map((e, i) => {
            const m = TIMELINE_KIND_META[e.kind] || TIMELINE_KIND_META.note;
            const Icon = m.icon;
            return (
              <li key={`${e.kind}-${e.id || i}`} className="pl-6 relative" data-testid={`timeline-event-${e.kind}-${e.id || i}`}>
                <span className={`absolute left-0 -translate-x-1/2 top-1 w-4 h-4 border ${m.border} bg-surface-base flex items-center justify-center`}>
                  <Icon size={9} weight="fill" className={m.color} />
                </span>
                <div className="border border-white/10 px-3 py-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className={`text-[10px] label-tiny ${m.color}`}>{m.label.toUpperCase()}</div>
                    <div className="text-[10px] text-text-secondary tabular-nums">{fmtDate(e.date)}</div>
                  </div>
                  <div className="text-sm font-heading mt-0.5">{e.title}</div>
                  {e.detail && <div className="text-xs text-text-secondary mt-0.5 whitespace-pre-wrap">{e.detail}</div>}
                  {e.link && <Link to={e.link} className="text-[10px] label-tiny text-brand-accent hover:underline mt-1 inline-block">OPEN ↗</Link>}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </Section>
  );
}
